import React, { useContext, useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import Signup from '../user/Signup';
import { useNavigate } from 'react-router';
import { WrapperContext } from '../context/WrapperContext';
import { AppContext } from '../window';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GroupIcon from '@mui/icons-material/Group';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AuthRedirect from '../components/AuthRedirect';
import ActivityFeed from './components/ActivityFeed';
import OfferCountdown from './components/OfferCountdown';
import TrustBadges from './components/TrustBadges';
import QuizFlow from './components/QuizFlow';
import ExitIntentPopup from './components/ExitIntentPopup';
import { preloadImage } from '../utils/preloadImage';
import { useTranslation } from '../i18n';

const LandingPageA: React.FC = () => {
    const navigate = useNavigate();
    const { setWrapperClasses } = useContext(WrapperContext);
    const { location } = useContext(AppContext);
    const [showQuiz, setShowQuiz] = useState(true);
    const [quizAnswers, setQuizAnswers] = useState<any>(null);
    const { t } = useTranslation();
    
    useEffect(() => {
        // Override wrapper classes to allow natural height (no vh-100)
        setWrapperClasses('100vh d-flex');
        
        // Reset to default when component unmounts
        return () => {
            setWrapperClasses('');
        };
    }, [setWrapperClasses]);

    // Preload hero background at high priority to improve LCP
    useEffect(() => {
        preloadImage('/images/landings/mature-bg9.webp', 'high');
    }, []);

    const handleSignupSuccess = () => {
        return navigate('/search');
    };

    const handleQuizComplete = (answers: any) => {
        setQuizAnswers(answers);
        setShowQuiz(false);
    };

    const handleExitIntentStay = () => {
        // Scroll to signup form when user chooses to stay
        const signupContainer = document.querySelector('.signup-container');
        if (signupContainer) {
            signupContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };


    return (
        <div
            className="landing-page-a flex-grow-1 d-flex align-items-center"
            style={{ position: 'relative' }}
        >
            {/* Hero background as an actual image so browser can prioritize it for LCP */}
            <img
                src="/images/landings/mature-bg9.webp"
                alt=""
                aria-hidden="true"
                fetchPriority="high"
                decoding="async"
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    zIndex: 0
                }}
            />
            <AuthRedirect />
            <ActivityFeed />
            <ExitIntentPopup onStay={handleExitIntentStay} />
            
            {/* Dark overlay for better text readability */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1
                }}
            />
            
            {/* Offer countdown */}
            <div className="position-absolute top-0 start-0 end-0 p-2 p-md-4 d-flex justify-content-center" style={{ zIndex: 3 }}>
                <OfferCountdown />
            </div>
            
            <Container fluid className="px-3 px-md-5" style={{ position: 'relative', zIndex: 2, paddingTop: '60px', paddingBottom: '2rem' }}>
                <Row className="justify-content-center align-items-center">
                    <Col xxl={10} xl={11}>
                        <Row className="align-items-center gx-5 gy-5">
                            {/* Left side - Quiz or Signup form */}
                            <Col lg={7} className="order-1 order-lg-1">
                                <div className={`signup-container bg-white rounded-4 shadow-lg mx-auto ${showQuiz ? 'with-quiz' : ''}`}>
                                    <div className="modal-content">
                                        {showQuiz ? (
                                            <>
                                                <div className="modal-header border-0 pb-0">
                                                    <h5 className="modal-title text-center w-100">{t('landing.perfect_match')}</h5>
                                                </div>
                                                <div className="modal-body pt-3">
                                                    <QuizFlow onComplete={handleQuizComplete} />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="modal-header border-0 pb-0">
                                                    <h5 className="modal-title">{t('landing.create_account')}</h5>
                                                </div>
                                                <div className="modal-body pt-3">
                                                    <Signup
                                                        onSignupSuccess={handleSignupSuccess}
                                                        submitButtonText={t('landing.start_meeting_singles')}
                                                        initialGender={quizAnswers?.gender}
                                                        initialAgeRange={quizAnswers?.ageRange}
                                                    />
                                                    <TrustBadges className="mt-3" />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Col>
                            
                            {/* Right side - Hero content */}
                            <Col lg={5} className="text-center text-lg-start order-2 order-lg-2">
                                <div className="hero-content text-white">
                                    <h1 className="display-5 fw-bold mb-3">
                                        {t('landing.exclusive_singles_location', { location: location?.town?.name || 'Your Area' })}
                                    </h1>
                                    <p className="lead mb-3">
                                        {t('landing.members_joined_week', { count: 427, location: location?.town?.name || 'your city' })}
                                        <span className="d-block text-warning fw-bold">{t('landing.meet_matches_distance')}</span>
                                    </p>
                                    <div className="hero-features ps-3">
                                        <div className="d-flex align-items-center mb-3">
                                            <VerifiedUserIcon className="me-3" sx={{ fontSize: '1.5rem' }} />
                                            <span>{t('landing.private_secure')}</span>
                                        </div>
                                        <div className="d-flex align-items-center mb-3">
                                            <GroupIcon className="me-3" sx={{ fontSize: '1.5rem' }} />
                                            <span>{t('landing.active_singles_near', { location: location?.town?.name || 'You' })}</span>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <FavoriteIcon className="me-3" sx={{ fontSize: '1.5rem' }} />
                                            <span className="badge bg-danger me-2">{t('landing.new_badge')}</span>
                                            <span>{t('landing.ai_matching')}</span>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default LandingPageA;